import { audioCtx } from "util/audioContext";
import convertDBtoLinear from "util/convertDBtoLinear";
import { EQPlaybackControls } from "../../types";
import generateInternalNodeState from "./generateInternalNodeState";
import { InternalNodeState } from "./types";

export default class Equalizer {
  private inputNode: GainNode;
  private outputNode: GainNode;
  private internalNodeState: InternalNodeState[] = [];

  constructor(audioPlayerId: number) {
    this.inputNode = audioCtx().createGain();
    this.outputNode = audioCtx().createGain();

    this.inputNode.connect(this.outputNode);
  }

  get node() {
    return this.inputNode;
  }

  public connect = (node: AudioNode) => {
    this.outputNode.connect(node);
  };

  public disconnect = () => {
    this.outputNode.disconnect();
  };

  public setValueAtTime = (eq: EQPlaybackControls, startTime: number) => {
    const orderedFrequencies = Object.keys(eq)
      .map((k) => Number(k))
      .sort((a, b) => a - b);

    this.inputNode.gain.linearRampToValueAtTime(
      getInputGainOffset(eq),
      startTime
    );

    // generate new internal state with the correct # of eq bands
    if (orderedFrequencies.length !== this.internalNodeState.length) {
      const newInternalNodeState = generateInternalNodeState(
        orderedFrequencies,
        eq,
        startTime
      );

      // connect to input and output
      this.inputNode.disconnect();
      this.inputNode.connect(newInternalNodeState[0].node);
      newInternalNodeState[newInternalNodeState.length - 1].node.connect(
        this.outputNode
      );

      // cleanup
      this.internalNodeState.forEach((n) => n.node.disconnect());
      this.internalNodeState = newInternalNodeState;
    } else {
      // update existing internal state without creating/deleting nodes
      orderedFrequencies.forEach((freq, i) => {
        const { frequency, q, type, gain } = eq[freq];
        const nodeState = this.internalNodeState[i];

        nodeState.freq = freq;
        nodeState.node.type = type;
        nodeState.node.frequency.linearRampToValueAtTime(frequency, startTime);
        nodeState.node.Q.linearRampToValueAtTime(q, startTime);
        nodeState.node.gain.linearRampToValueAtTime(gain, startTime);
      });
    }
  };
}

function getInputGainOffset(eq: EQPlaybackControls) {
  const totalGain = Object.values(eq).reduce((sum, { gain }) => sum + gain, 0);
  const reversedGain = totalGain * -1;
  const autoMakeupGainMultiplier = 0.33333; // A good enough magic number that keeps most sounds level

  return convertDBtoLinear(reversedGain * autoMakeupGainMultiplier);
}
